/* eslint-disable no-fallthrough */
import { Dispatch, useReducer } from 'react';

import { ContentParametersBulkUpdateMode, ContentParametersMapping, ParameterRange } from 'data/generated/graphql';

export interface MappingState {
  originalMappings: ContentParametersMapping[];
  modifiedMappings: ContentParametersMapping[];
  bulkUpdate: {
    treatmentType: string;
    mode: ContentParametersBulkUpdateMode;
    artifactNames: string[];
  }[];
  bulkUpdateRestore: {
    treatmentType: string;
    allessehIds: Record<string, string>;
  };
  historyIndexes: {
    [treatmentType: string]: number | null;
  };
}

export type MappingAction =
  | {
      type: 'UPDATE_PARAMETERS';
      payload: {
        treatmentType: string;
        value: ParameterRange[];
      };
    }
  | {
      type: 'UPDATE_DEPRECATED';
      payload: {
        treatmentType: string;
        value: boolean;
      };
    }
  | {
      type: 'UPDATE_COMMENT';
      payload: {
        treatmentType: string;
        value: string;
      };
    }
  | {
      type: 'UNDO_CHANGES';
      payload: {
        treatmentType: string;
      };
    }
  | {
      type: 'SET_MAPPINGS';
      payload: {
        mappings: ContentParametersMapping[];
      };
    }
  | {
      type: 'APPLY_MODIFIED_MAPPING';
      payload: {
        treatmentType: string;
      };
    }
  | {
      type: 'SORT_TREATMENT_TYPES';
      payload: {
        sortedTreatmentList: string[];
      };
    }
  | {
      type: 'SET_BULK_UPDATE_CONFIGS';
      payload: {
        configs: {
          treatmentType: string;
          mode: ContentParametersBulkUpdateMode;
          artifactNames: string[];
        }[];
      };
    }
  | {
      type: 'BULK_UPDATE_SET_MODE';
      payload: {
        treatmentType: string;
        mode: ContentParametersBulkUpdateMode;
        keepRestore?: boolean;
      };
    }
  | {
      type: 'BULK_UPDATE_SET_ALLESSEH_IDS';
      payload: {
        treatmentType: string;
        artifactNames: string[];
        keepRestore?: boolean;
      };
    }
  | {
      type: 'BULK_UPDATE_RESTORE';
      payload: {
        treatmentType: string;
        allessehId: string;
      };
    }
  | {
      type: 'SET_HISTORY_INDEX';
      payload: {
        treatmentType: string;
        index: number | null;
      };
    }
  | {
      type: 'RESET_HISTORY_INDEX';
      payload: {
        treatmentType: string;
      };
    }
  | {
      type: 'CLEAR_HISTORY_INDEXES';
    };

function sortTreatmentTypesFn(action: Extract<MappingAction, { type: 'SORT_TREATMENT_TYPES' }>) {
  return (a: ContentParametersMapping, b: ContentParametersMapping) => {
    const indexA = action.payload.sortedTreatmentList.indexOf(a.treatmentType);
    const indexB = action.payload.sortedTreatmentList.indexOf(b.treatmentType);

    if (indexA === -1 && indexB === -1) {
      return a.treatmentType.localeCompare(b.treatmentType);
    }
    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }
    return indexA - indexB;
  };
}

function reducer(mappingState: MappingState, action: MappingAction) {
  let fieldToUpdate: 'parameters' | 'deprecated' | 'comment' = 'comment';
  switch (action.type) {
    case 'UPDATE_PARAMETERS':
      fieldToUpdate = 'parameters';
      break;
    case 'UPDATE_DEPRECATED':
      fieldToUpdate = 'deprecated';
      break;
    case 'UPDATE_COMMENT':
      fieldToUpdate = 'comment';
      break;
    default:
      break;
  }

  switch (action.type) {
    case 'SET_HISTORY_INDEX': {
      return {
        ...mappingState,
        historyIndexes: {
          ...mappingState.historyIndexes,
          [(action.payload as { treatmentType: string }).treatmentType]: (
            action.payload as {
              index: number | null;
            }
          ).index
        }
      };
    }
    case 'RESET_HISTORY_INDEX': {
      return {
        ...mappingState,
        historyIndexes: {
          ...mappingState.historyIndexes,
          [(action.payload as { treatmentType: string }).treatmentType]: null
        }
      };
    }
    case 'CLEAR_HISTORY_INDEXES': {
      return {
        ...mappingState,
        historyIndexes: {}
      };
    }
    case 'SET_MAPPINGS':
      return {
        ...mappingState,
        originalMappings: action.payload.mappings,
        modifiedMappings: action.payload.mappings
      };
    case 'SET_BULK_UPDATE_CONFIGS':
      return {
        ...mappingState,
        bulkUpdate: action.payload.configs
      };
    case 'APPLY_MODIFIED_MAPPING':
      const modifiedMapping = mappingState.modifiedMappings.find(
        (mapping) => mapping.treatmentType === (action.payload as { treatmentType: string }).treatmentType
      );

      return {
        ...mappingState,
        originalMappings: mappingState.originalMappings.map((mapping) =>
          mapping.treatmentType === action.payload.treatmentType
            ? (modifiedMapping as ContentParametersMapping)
            : mapping
        )
      };
    case 'UPDATE_PARAMETERS':
    case 'UPDATE_DEPRECATED':
    case 'UPDATE_COMMENT': {
      const modifiedMapping = mappingState.modifiedMappings.find(
        (mapping) => mapping.treatmentType === (action.payload as { treatmentType: string }).treatmentType
      );

      if (modifiedMapping) {
        const updatedMapping = { ...modifiedMapping, [fieldToUpdate]: action.payload.value };
        const updatedModifiedMappings = mappingState.modifiedMappings.map((m) =>
          m.treatmentType === action.payload.treatmentType ? updatedMapping : m
        );
        return { ...mappingState, modifiedMappings: updatedModifiedMappings };
      }

      throw new Error(
        `Mapping with treatmentType ${action.payload.treatmentType} not found when attempting to update ${fieldToUpdate} field.`
      );
    }
    case 'UNDO_CHANGES': {
      const originalMapping = mappingState.originalMappings.find(
        (mapping) => mapping.treatmentType === (action.payload as { treatmentType: string }).treatmentType
      );

      if (originalMapping) {
        const updatedModifiedMappings = mappingState.modifiedMappings.map((mapping) =>
          mapping.treatmentType === action.payload.treatmentType ? originalMapping : mapping
        );

        return {
          ...mappingState,
          modifiedMappings: updatedModifiedMappings,
          bulkUpdate: mappingState.bulkUpdate.map((bulkUpdateConfig) =>
            bulkUpdateConfig.treatmentType === action.payload.treatmentType
              ? { ...bulkUpdateConfig, mode: ContentParametersBulkUpdateMode.None, artifactNames: [] }
              : bulkUpdateConfig
          ),
          bulkUpdateRestore: {
            ...mappingState.bulkUpdateRestore,
            treatmentType: '',
            allessehIds: {
              ...mappingState.bulkUpdateRestore.allessehIds,
              [action.payload.treatmentType]: ''
            }
          }
        };
      }

      throw new Error(
        `Mapping with treatmentType ${action.payload.treatmentType} not found when attempting to undo changes.`
      );
    }
    case 'SORT_TREATMENT_TYPES': {
      return {
        ...mappingState,
        originalMappings: mappingState.originalMappings.slice().sort(sortTreatmentTypesFn(action)),
        modifiedMappings: mappingState.modifiedMappings.slice().sort(sortTreatmentTypesFn(action))
      };
    }
    case 'BULK_UPDATE_SET_MODE': {
      const update = {
        ...mappingState,
        bulkUpdate: mappingState.bulkUpdate.map((bulkUpdateConfig) =>
          bulkUpdateConfig.treatmentType === action.payload.treatmentType
            ? { ...bulkUpdateConfig, mode: action.payload.mode }
            : bulkUpdateConfig
        )
      };

      if (!action.payload.keepRestore) {
        update.bulkUpdateRestore = {
          ...update.bulkUpdateRestore,
          treatmentType: '',
          allessehIds: {
            ...update.bulkUpdateRestore.allessehIds,
            [action.payload.treatmentType]: ''
          }
        };
      }

      return update;
    }
    case 'BULK_UPDATE_SET_ALLESSEH_IDS': {
      const update = {
        ...mappingState,
        bulkUpdate: mappingState.bulkUpdate.map((bulkUpdateConfig) =>
          bulkUpdateConfig.treatmentType === action.payload.treatmentType
            ? { ...bulkUpdateConfig, artifactNames: action.payload.artifactNames }
            : bulkUpdateConfig
        )
      };

      if (!action.payload.keepRestore) {
        update.bulkUpdateRestore = {
          ...update.bulkUpdateRestore,
          treatmentType: '',
          allessehIds: {
            ...update.bulkUpdateRestore.allessehIds,
            [action.payload.treatmentType]: ''
          }
        };
      }

      return update;
    }
    case 'BULK_UPDATE_RESTORE': {
      return {
        ...mappingState,
        bulkUpdateRestore: {
          ...mappingState.bulkUpdateRestore,
          treatmentType: action.payload.treatmentType,
          allessehIds: {
            ...mappingState.bulkUpdateRestore.allessehIds,
            [action.payload.treatmentType]: action.payload.allessehId
          }
        }
      };
    }
    default:
      return mappingState;
  }
}

export type MappingActionDispatchFn = Dispatch<MappingAction>;

export function useMappingState(initialMappings: ContentParametersMapping[]) {
  const [mappingState, dispatch] = useReducer(reducer, initialMappings, (initialMappings) => ({
    originalMappings: initialMappings,
    modifiedMappings: initialMappings,
    bulkUpdate: [],
    bulkUpdateRestore: {
      treatmentType: '',
      allessehIds: {}
    },
    historyIndexes: {}
  }));

  return { mappingState, dispatch };
}
